import { isRouteErrorResponse, useRouteError } from "@remix-run/react";
import { Deviations, Whoops } from "@ruter-ds-next/illustrations";

import { PageWrapper } from "./PageWrapper";

export function DefaultErrorBoundary(
  props: React.ComponentPropsWithoutRef<"div">
) {
  const error = useRouteError();

  /**
   * Error caused by _us_ throwing a Response object
   * in our route handlers
   */
  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      return <UnauthorizedErrorMessage />;
    }
    return (
      <ValidationErrorMessage {...props}>{error?.data}</ValidationErrorMessage>
    );
  }

  /**
   * Error caused by unexpected errors
   */
  if (error instanceof Error) {
    return (
      <PageWrapper>
        <UnexpectedErrorMessage {...props}>
          {`${error?.message}\n\n${error?.stack ?? ""}`}
        </UnexpectedErrorMessage>
      </PageWrapper>
    );
  }

  /**
   * Something else was thrown, this is super weird 🤯
   */
  // eslint-disable-next-line no-console
  console.error(error);
  return (
    <PageWrapper>
      <UnexpectedErrorMessage {...props} />
    </PageWrapper>
  );
}

export function ValidationErrorMessage({
  children,
  className = "",
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={`mx-auto max-w-sm rounded-lg border-2 border-decorative-butter bg-decorative-champagne px-4 py-2 text-left ${className}`}
      {...props}
    >
      <div className="flex flex-col items-center justify-between gap-x-4 gap-y-8 text-center sm:flex-row sm:flex-nowrap sm:text-left">
        <div>
          <h2 className="mb-0 mt-2 font-title-s-md text-title-s-md">Wooops</h2>
          <div className="mt-2 max-w-[300px]">{children}</div>
        </div>
        <Deviations className="size-32 shrink-0" />
      </div>
    </div>
  );
}

export function ValidationErrorFrame({
  children,
  className = "",
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={`mx-auto rounded-lg border-2 border-decorative-butter bg-decorative-champagne px-4 py-2 text-left ${className}`}
      {...props}
    >
      <div className="flex flex-col items-center justify-between gap-x-4 gap-y-8 whitespace-pre-wrap sm:flex-row sm:flex-nowrap">
        <div className="w-full">{children}</div>
      </div>
    </div>
  );
}

export function UnauthorizedErrorMessage() {
  return (
    <div className="mx-auto max-w-sm rounded-lg border-decorative-butter bg-decorative-champagne px-4 py-2 text-left">
      <h2 className="my-2 font-title-s-md text-title-s-md text-decorative-coral">
        Du har ikke tilgang
      </h2>
      <p>
        Send en mail til{" "}
        <a href="mailto:rdp-support@ruter.no?subject=Hjelp%20med%20vvs%20login">
          rdp-support@ruter.no
        </a>{" "}
        dersom du trenger tilgang.
      </p>
    </div>
  );
}

export function UnexpectedErrorMessage({
  children,
  className = "",
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={`mx-auto max-w-sm px-4 py-2 text-left ${className}`}
      {...props}
    >
      <div className="flex flex-col items-center justify-between gap-x-4 gap-y-8 text-center sm:flex-row sm:flex-nowrap sm:text-left">
        <div>
          <h2 className="mb-0 mt-2 font-title-s-md text-title-s-md text-decorative-coral">
            Oh, no!
          </h2>
          <p className="mt-2 max-w-[300px]">
            En uvented feil skjedde hos oss! Vi er blitt varslet og ser på det.
          </p>
        </div>
        <Whoops className="mx-auto size-44 shrink-0 sm:mx-8" />
      </div>
      <hr className="my-8 border-light-ink-secondary" />
      <pre className="my-4 overflow-x-scroll text-left">{children}</pre>
    </div>
  );
}
